import React, { useMemo } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { formatTimestampForZone2 } from "../utils/formattimestamp";
import { PhoneIncoming, PhoneOutgoing, Voicemail } from "lucide-react";

const getCallDate = (callTime) => {
  if (!callTime || callTime === "N/A") return null;
  const str = String(callTime);
  if (str.length === 10) {
    return formatTimestampForZone2(Number(callTime) * 1000);
  }
  if (str.length === 13) {
    return formatTimestampForZone2(parseInt(Number(callTime), 10));
  }
  return formatTimestampForZone2(Number(callTime));
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const dayKey = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Build L7D count + WoW + weekly sparkline over last ~30 days */
const computeMetric = (calls, predicate) => {
  const today = startOfDay(new Date());
  const l7dStart = new Date(today);
  l7dStart.setDate(today.getDate() - 6);
  const prevStart = new Date(today);
  prevStart.setDate(today.getDate() - 13);
  const prevEnd = new Date(today);
  prevEnd.setDate(today.getDate() - 7);

  // ~30 days → 4 weekly buckets ending today (oldest → newest)
  const weekCount = 4;
  const weeks = [];
  for (let i = weekCount - 1; i >= 0; i--) {
    const end = new Date(today);
    end.setDate(today.getDate() - i * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    weeks.push({
      start: startOfDay(start),
      end: startOfDay(end),
      value: 0,
      label: `${dayKey(start)} – ${dayKey(end)}`,
    });
  }

  let l7dCount = 0;
  let prevCount = 0;
  let hasDataBeforeL7d = false;

  (calls || []).forEach((call) => {
    if (!predicate(call)) return;
    const callDate = getCallDate(call.callTime);
    if (!callDate) return;
    const day = startOfDay(callDate);

    if (day < l7dStart) hasDataBeforeL7d = true;

    if (day >= l7dStart && day <= today) {
      l7dCount += 1;
    } else if (day >= prevStart && day <= prevEnd) {
      prevCount += 1;
    }

    for (const week of weeks) {
      if (day >= week.start && day <= week.end) {
        week.value += 1;
        break;
      }
    }
  });

  const sparkline = weeks.map((w) => ({
    date: w.label,
    value: w.value,
  }));

  let wowPct = null;
  if (hasDataBeforeL7d || prevCount > 0) {
    if (prevCount === 0 && l7dCount === 0) {
      wowPct = 0;
    } else if (prevCount === 0) {
      wowPct = 100;
    } else {
      wowPct = Math.round(((l7dCount - prevCount) / prevCount) * 100);
    }
  }

  return { count: l7dCount, sparkline, wowPct, prevCount };
};

const WowBadge = ({ pct }) => {
  if (pct == null) return null;
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat
    ? "text-gray-500"
    : up
      ? "text-emerald-600"
      : "text-rose-600";
  const arrow = flat ? "→" : up ? "↑" : "↓";

  return (
    <div className={`mt-1.5 text-xs font-medium ${color}`}>
      {arrow} {Math.abs(pct)}% vs prior week
    </div>
  );
};

const SparkTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md bg-gray-900 text-white px-2.5 py-1.5 shadow-lg text-xs">
      <div className="text-gray-300 mb-0.5">{point.date}</div>
      <div className="font-semibold tabular-nums">
        {point.value} {point.value === 1 ? "call" : "calls"}
      </div>
    </div>
  );
};

const MetricCard = ({
  title,
  id,
  value,
  color,
  icon: Icon,
  data,
  wowPct,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-1 min-w-[180px] text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 hover:shadow transition-all p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <Icon className="w-4 h-4 shrink-0" style={{ color }} />
          <span>{title}</span>
        </div>
        <div className="text-2xl font-semibold text-gray-900 tabular-nums">
          {value}
        </div>
        <div className="text-[11px] text-gray-400 mt-0.5">Last 7 days</div>
        <WowBadge pct={wowPct} />
      </div>
      <div
        className="w-[100px] h-[48px] shrink-0 mt-1"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
            <defs>
              <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              content={<SparkTooltip />}
              cursor={{ stroke: color, strokeWidth: 1, strokeOpacity: 0.35 }}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ zIndex: 20, outline: "none" }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#spark-${id})`}
              dot={false}
              activeDot={{ r: 3, fill: color, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  </button>
);

const CallMetricCards = ({ calls = [], isLoading, onOpenChart }) => {
  const metrics = useMemo(() => {
    const defs = [
      {
        id: "inbound",
        title: "Inbound calls",
        color: "#8B5CF6",
        icon: PhoneIncoming,
        predicate: (c) => c.action !== "Outbound Call",
      },
      {
        id: "outbound",
        title: "Outbound calls",
        color: "#F59E0B",
        icon: PhoneOutgoing,
        predicate: (c) => c.action === "Outbound Call",
      },
      {
        id: "voicemails",
        title: "Voicemails",
        color: "#6366F1",
        icon: Voicemail,
        predicate: (c) => c.voicemail === "Yes",
      },
    ];

    return defs.map((d) => {
      const { count, sparkline, wowPct } = computeMetric(calls, d.predicate);
      return { ...d, count, data: sparkline, wowPct };
    });
  }, [calls]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      {metrics.map((m) => (
        <MetricCard
          key={m.id}
          id={m.id}
          title={m.title}
          value={isLoading ? "—" : m.count}
          color={m.color}
          icon={m.icon}
          data={m.data}
          wowPct={isLoading ? null : m.wowPct}
          onClick={() => onOpenChart?.(m.id)}
        />
      ))}
    </div>
  );
};

export default CallMetricCards;
